/**
 * Retold Application Boot
 *
 * Builds and starts a browser app entirely from the server's /1.0/AppManifest,
 * so an app ships no client JavaScript at all. The host page is:
 *
 *   <script src="pict.min.js"></script>
 *   <script src="retold-application.min.js"></script>
 *   <script>RetoldApplicationFoundation.boot();</script>
 *
 * Derivation rules (the shape Steven chose):
 *   - AUTO for CRUD: every entity the manifest reports becomes a record set, so
 *     the API surface and deep links are complete the moment a table exists.
 *   - OPT-IN for nav: only entities listed in the manifest's Navigation appear in
 *     the sidebar, so lookup and join tables never show up unintentionally.
 */

const libRetoldApplication = require('./Retold-Application.js');

// Prefer the Pict already on the page (loaded as pict.min.js); fall back to the
// module for a bundled or test context.
function resolvePict()
{
	if (typeof window !== 'undefined' && window.Pict) { return window.Pict; }
	return require('pict');
}

/**
 * Turn the manifest's entity names into the descriptor shape the chassis
 * translates into MeadowEndpoint record sets. Only the name is needed: recordset
 * derives every column from the /1.0/<Entity>/Schema endpoint at runtime.
 */
function entitiesFromManifest(pManifest)
{
	let tmpEntities = (pManifest && pManifest.Entities) || [];
	let tmpDescriptors = {};

	if (Array.isArray(tmpEntities))
	{
		tmpEntities.forEach((pEntity) =>
		{
			if (typeof pEntity === 'string') { tmpDescriptors[pEntity] = { Key: pEntity }; }
			else if (pEntity && pEntity.Key) { tmpDescriptors[pEntity.Key] = pEntity; }
		});
		return tmpDescriptors;
	}
	// An object of per-entity overrides.
	Object.keys(tmpEntities).forEach((pKey) =>
	{
		tmpDescriptors[pKey] = Object.assign({ Key: pKey }, tmpEntities[pKey]);
	});
	return tmpDescriptors;
}

/**
 * Navigation is opt-in: each manifest entry names an entity (routed to its record
 * set list) or carries an explicit Route.
 */
function navigationFromManifest(pManifest)
{
	let tmpNavigation = (pManifest && pManifest.Navigation) || [];
	return tmpNavigation.map((pNode) =>
	{
		if (typeof pNode === 'string') { return { Name: pNode, Route: '/PSRS/' + pNode + '/List' }; }
		let tmpRoute = pNode.Route || (pNode.Entity ? ('/PSRS/' + pNode.Entity + '/List') : '');
		// SystemAdmin entries are gated by the shell to system administrators (see PictView-Retold-Shell).
		return { Name: pNode.Name || pNode.Entity || tmpRoute, Route: tmpRoute, SystemAdmin: !!pNode.SystemAdmin };
	}).filter((pNode) => pNode.Route);
}

/**
 * Build an application class from a manifest. Exposed separately so a test (or an
 * app with genuine business views) can extend the result.
 */
function applicationClassFromManifest(pManifest, pOptions)
{
	let tmpManifest = pManifest || {};
	let tmpOptions = pOptions || {};

	class RetoldManifestApplication extends (tmpOptions.ApplicationClass || libRetoldApplication)
	{
	}

	RetoldManifestApplication.default_configuration = Object.assign(
		{
			Name: tmpManifest.Title || tmpManifest.Product || 'Retold Application',
			Hash: tmpManifest.Hash || tmpManifest.Product || 'RetoldApplication',
			Entities: entitiesFromManifest(tmpManifest),
			// Per-entity schemas the server bundled into the manifest, keyed by entity name.
			// Handed to each record set so it does not fetch /1.0/<Entity>/Schema on boot; a
			// record set with no schema here falls back to fetching, exactly as before.
			EntitySchemas: tmpManifest.EntitySchemas || null,
			SiteMap: navigationFromManifest(tmpManifest),
			Brand: tmpManifest.Brand || null,
			APIPrefix: tmpManifest.APIPrefix || '/1.0/',
			Auth: !!(tmpManifest.Auth && tmpManifest.Auth.Enabled),
			// When the server reports two-step login is on, the auth client and the sign-in
			// view switch to the /Login flow (password, then a code).
			AuthTwoStepLogin: !!(tmpManifest.Auth && tmpManifest.Auth.TwoStepLogin),
			// First-run appliance install: a fresh server with no account yet reports SetupRequired,
			// and may carry a Setup block that re-skins the first-run screen's copy/fields.
			SetupRequired: !!tmpManifest.SetupRequired,
			Setup: tmpManifest.Setup || null
		}, tmpOptions.Configuration || {});

	return RetoldManifestApplication;
}

/**
 * Fetch the manifest, build the app, and start it.
 * @returns {Promise<object>} the running application
 */
function boot(pOptions)
{
	let tmpOptions = pOptions || {};
	let tmpManifestURL = tmpOptions.ManifestURL || '/1.0/AppManifest';

	return fetch(tmpManifestURL, { credentials: 'include', headers: { 'Accept': 'application/json' } })
		.then((pResponse) => pResponse.json())
		.then((pManifest) => bootWithManifest(pManifest, tmpOptions))
		.catch((pError) =>
		{
			// eslint-disable-next-line no-console
			console.error('Retold boot: could not load the app manifest from ' + tmpManifestURL + ': ' + ((pError && pError.message) || pError));
			return null;
		});
}

/**
 * Start an app from an already-loaded manifest.
 */
function bootWithManifest(pManifest, pOptions)
{
	let tmpOptions = pOptions || {};
	let libPict = resolvePict();

	let tmpApplicationClass = applicationClassFromManifest(pManifest, tmpOptions);
	let tmpPict = tmpOptions.Pict || new libPict(tmpOptions.PictConfiguration || {});

	// Instantiate through addApplication rather than a raw `new`. Both run the same
	// constructor, but addApplication is what registers the instance as the default
	// PictApplication service, which sets pict.PictApplication. That reference is a
	// standard pict contract -- views and the pict-section-form layout providers read
	// pict.PictApplication.options (e.g. Pict-Layout-Record reads WidthQuantization
	// from it while building a record form). A raw construction leaves it null, so the
	// first record detail/edit render throws "Cannot read properties of null". This
	// mirrors what Pict.safeLoadPictApplication does for a hand-written app.
	let tmpApplicationHash = (tmpApplicationClass.default_configuration && tmpApplicationClass.default_configuration.Hash) || 'RetoldApplication';
	let tmpApplication = tmpPict.addApplication(tmpApplicationHash, tmpApplicationClass.default_configuration, tmpApplicationClass);
	if (typeof window !== 'undefined') { window._Pict = tmpPict; }

	return new Promise((fResolve) =>
	{
		tmpApplication.initializeAsync(() => fResolve(tmpApplication));
	});
}

module.exports = { boot, bootWithManifest, applicationClassFromManifest, entitiesFromManifest, navigationFromManifest };
